import { useState } from 'react';
import { apiClient } from '@config/api';

const ERROR_MESSAGE_GENERIC = {
  title: 'COMMON:ERRORS:GENERIC_ERROR_TITLE',
  description: 'COMMON:ERRORS:DEFAULT_ERROR',
  details: [],
};

const ERROR_DETAILS_PREFIX = 'CLIENTS:CREATE_CLIENT:ERROR_DETAILS';

const ERROR_MESSAGE_MAP = {
  'Invalid CUIT': `${ERROR_DETAILS_PREFIX}:INVALID_CUIT`,
  'customerType must be PRIVATE or PUBLIC': `${ERROR_DETAILS_PREFIX}:INVALID_CUSTOMER_TYPE`,
  'Company already exist.': `${ERROR_DETAILS_PREFIX}:EXIST_COMPANY_PROCESS_RESULT`,
  'CompanyKey already exist.': `${ERROR_DETAILS_PREFIX}:EXIST_SEGMENT_RESULT`,
  'Invalid address': `${ERROR_DETAILS_PREFIX}:INVALID_ADDRESS`,
  'CUIT already exists as customer': `${ERROR_DETAILS_PREFIX}:CUIT_ALREADY_EXISTS_AS_CUSTOMER`,
  'CUIT already has a pending request': `${ERROR_DETAILS_PREFIX}:CUIT_PENDING_REQUEST`,
  'CUIT already has an approved request': `${ERROR_DETAILS_PREFIX}:CUIT_APPROVED_REQUEST`,
};

const DATABASE_ERROR_CODES = ['database_error', '3000'];

/**
 * Maps a backend error to translation keys.
 * Supports strings, `{ message, code }`, and nested `{ errors: [...] }`.
 * @param {string | { message?: string, code?: string, errors?: Array }} error
 * @returns {string[]}
 */
const mapErrorToTranslationKeys = (error) => {
  if (typeof error === 'string') {
    const translationKey = ERROR_MESSAGE_MAP[error];
    return translationKey ? [translationKey] : [];
  }

  if (!error || typeof error !== 'object') {
    return [];
  }

  if (DATABASE_ERROR_CODES.includes(error.code)) {
    return [`${ERROR_DETAILS_PREFIX}:DATABASE_ERROR`];
  }

  const nestedErrors = Array.isArray(error.errors)
    ? error.errors.flatMap(mapErrorToTranslationKeys)
    : [];

  if (typeof error.message === 'string') {
    const translationKey = ERROR_MESSAGE_MAP[error.message];
    return translationKey ? [translationKey, ...nestedErrors] : nestedErrors;
  }

  return nestedErrors;
};

/**
 * Extracts user-facing error translation keys from `/partner/customer/add-client` error payloads.
 * Errors may be strings, `{ message, code }`, or nested `{ errors: [{ message, code }] }`.
 * @param {Array<string | object>} errors
 * @param {string} [responseCode]
 * @returns {string[]}
 */
const getErrorAlertMessage = (errors = [], responseCode) => {
  if (DATABASE_ERROR_CODES.includes(responseCode)) {
    return [`${ERROR_DETAILS_PREFIX}:DATABASE_ERROR`];
  }

  if (!Array.isArray(errors)) {
    return [];
  }

  return errors.flatMap(mapErrorToTranslationKeys);
};

/**
 * Custom hook to manage prospect creation and confirmation.
 * @returns {object} Object containing data, loading state, errors, and functions for creating and confirming prospects.
 */
function useAddClient() {
  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: false,
    alertMessage: ERROR_MESSAGE_GENERIC,
  });

  /**
   * Function to set the error state.
   * @param {boolean} error - The error state.
   */
  const setIsError = (error) => {
    setStatus({ ...status, error });
  };

  /**
   * Function to set the error state.
   * @param {boolean} success - The success state.
   */
  const setIsSuccess = (success) => {
    setStatus({ ...status, success });
  };

  /**
   * Function to handle customer group
   */
  const handleCustomerGroup = (companyGroup) => {
    if (typeof companyGroup === 'object') {
      return Object.keys(companyGroup).length
        ? { id: companyGroup.id, name: companyGroup.title }
        : undefined;
    } else {
      return companyGroup ? { name: companyGroup } : undefined;
    }
  };

  /**
   * Function to create a fast dispersion.
   * @param {prospects} body - The list prospects for fast dispersion.
   * @param {prospects} amount - The amount body for fast dispersion.
   * @param {prospects} companyId - The companyId for fast dispersion.
   * @returns {Promise<object>} A Promise that resolves with the response data once the mutation is complete.
   */
  const addClient = async ({
    companyGroup,
    companyName,
    cuit,
    customerType,
    segment,
    city,
    street,
    number,
    floor,
    apartment,
    postalCode,
    province,
  }) => {
    setStatus({
      loading: false,
      success: false,
      error: false,
      alertMessage: ERROR_MESSAGE_GENERIC,
    });
    try {
      const customerGroup = companyGroup && handleCustomerGroup(companyGroup);
      const { data } = await apiClient.post('/partner/customer/add-client', {
        ...(customerGroup && { customerGroup }),
        customerName: companyName,
        cuit,
        companyKey: segment,
        customerType: customerType.value,
        address: {
          street,
          number,
          floor,
          apartment,
          postalCode,
          stateId: province,
          city: city?.id ? { cityId: city.id } : { cityName: city },
        },
      });

      setStatus({
        loading: false,
        success: true,
        error: false,
        alertMessage: {
          description: 'CLIENTS:CREATE_CLIENT:SUCCESS_DESCRIPTION',
          details: [],
        },
      });
      return data;
    } catch (error) {
      const responseData = error?.response?.data;
      const errorAlertMessage = getErrorAlertMessage(responseData?.errors, responseData?.code);
      const isDatabaseError = DATABASE_ERROR_CODES.includes(responseData?.code);

      setStatus({
        loading: false,
        success: false,
        error: true,
        alertMessage: {
          title: 'COMMON:ERRORS:GENERIC_ERROR_TITLE',
          description: isDatabaseError
            ? `${ERROR_DETAILS_PREFIX}:DATABASE_ERROR`
            : errorAlertMessage.length > 0
            ? 'CLIENTS:CREATE_CLIENT:ERROR_DESCRIPTION'
            : 'COMMON:ERRORS:DEFAULT_ERROR',
          details: isDatabaseError ? [] : errorAlertMessage,
        },
      });
    }
  };

  const { loading: isLoading, success: isSuccess, error: isError, alertMessage } = status;

  return { isLoading, isSuccess, isError, alertMessage, setIsSuccess, setIsError, addClient };
}

export default useAddClient;
